import { Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { MainLayout } from './layouts/MainLayout';
import { AdminLayout } from './layouts/AdminLayout';
import { navigationConfig, filterNavigationByPermissions } from './config/navigation';
import { adminNavigation, filterAdminNavigationByPermissions } from '@/config/adminNavigation';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './components/ui/Toast';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { PlaceholderPage } from './pages/PlaceholderPage';
import { ProfilePage } from './pages/ProfilePage';
import { UserSettingsPage } from './pages/UserSettingsPage';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { UsersListPage } from './pages/admin/UsersListPage';
import { UserDetailPage } from './pages/admin/UserDetailPage';
import { RolesListPage } from './pages/admin/RolesListPage';
import { PermissionsCatalogPage } from './pages/admin/PermissionsCatalogPage';
import { AuditLogPage } from './pages/admin/AuditLogPage';
import { SettingsPage } from './pages/admin/SettingsPage';
import { SystemStatusPage } from './pages/admin/SystemStatusPage';
import { DepartmentManagementPage } from './pages/admin/DepartmentManagementPage';
import { PositionManagementPage } from './pages/admin/PositionManagementPage';
import { EmployeesListPage } from './pages/personal/EmployeesListPage';
import { EmployeeDetailPage } from './pages/personal/EmployeeDetailPage';
import { EmployeeCreatePage } from './pages/personal/EmployeeCreatePage';

const sectionPermissions: Record<string, string> = {
  '/personal': 'personnel.read',
  '/patienten': 'patients.read',
  '/medizin': 'medical.read',
  '/organisation': 'calendar.read',
  '/wissen': 'documents.read',
  '/kommunikation': 'news.read',
  '/notfall': 'emergency.read',
  '/betrieb': 'material.read',
  '/verwaltung': 'administration.read',
};

function SectionPlaceholder({ path }: { path: string }) {
  const sectionNames: Record<string, string> = {
    '/personal': 'Personal',
    '/patienten': 'Patienten',
    '/medizin': 'Medizin',
    '/organisation': 'Organisation',
    '/wissen': 'Wissen',
    '/kommunikation': 'Kommunikation',
    '/notfall': 'Sicherheit & Notfall',
    '/betrieb': 'Betrieb',
    '/verwaltung': 'Verwaltung',
    '/system': 'System',
  };

  return <PlaceholderPage section={sectionNames[path] || 'Bereich'} />;
}

function AdminRoutes() {
  const { isLoading, permissions } = useAuth();
  const navigation = filterNavigationByPermissions(navigationConfig, permissions);
  const adminNav = filterAdminNavigationByPermissions(adminNavigation, permissions);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-950">
        <div className="flex items-center gap-3 text-sm font-medium text-neutral-600 dark:text-neutral-300">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" aria-hidden="true" />
          Intranet wird geladen...
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<MainLayout navigation={navigation} />}>
        <Route
          path="/"
          element={
            <ProtectedRoute requiredPermission="dashboard.view">
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profil"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/einstellungen"
          element={
            <ProtectedRoute>
              <UserSettingsPage />
            </ProtectedRoute>
          }
        />
        {sectionRoutes.map(({ path, permission, sectionPath }) => (
          <Route
            key={path}
            path={`${path}/*`}
            element={
              <ProtectedRoute requiredPermission={permission}>
                <SectionPlaceholder path={sectionPath} />
              </ProtectedRoute>
            }
          />
        ))}
        {Object.entries(sectionPermissions).map(([path, permission]) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute requiredPermission={permission}>
                <SectionPlaceholder path={path} />
              </ProtectedRoute>
            }
          />
        ))}
<Route
            path="/system"
            element={
              <ProtectedRoute requiredPermission="system.status">
                <SectionPlaceholder path="/system" />
              </ProtectedRoute>
            }
          />
          {/* Personal → Mitarbeiter routes */}
          <Route
            path="/personal/mitarbeiter"
            element={
              <ProtectedRoute requiredPermission="personnel.read">
                <EmployeesListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/personal/mitarbeiter/neu"
            element={
              <ProtectedRoute requiredPermission="personnel.create">
                <EmployeeCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/personal/mitarbeiter/:id"
            element={
              <ProtectedRoute requiredPermission="personnel.read">
                <EmployeeDetailPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* Admin Routes */}
      <Route element={<AdminLayout navigation={adminNav} />}>
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredPermission="permissions.read">
              <AdminDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/access/users"
          element={
            <ProtectedRoute requiredPermission="users.read">
              <UsersListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/access/users/:id"
          element={
            <ProtectedRoute requiredPermission="users.read">
              <UserDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/access/roles"
          element={
            <ProtectedRoute requiredPermission="permissions.read">
              <RolesListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/access/permissions"
          element={
            <ProtectedRoute requiredPermission="permissions.read">
              <PermissionsCatalogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/security/audit"
          element={
            <ProtectedRoute requiredPermission="audit.read">
              <AuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/security/sessions"
          element={
            <ProtectedRoute requiredPermission="audit.read">
              <div className="p-8 text-center">
                <p className="text-neutral-500 dark:text-neutral-400">Sitzungsübersicht - wird in einer späteren Version implementiert.</p>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/organization/departments"
          element={
            <ProtectedRoute requiredPermission="departments.read">
              <DepartmentManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/organization/positions"
          element={
            <ProtectedRoute requiredPermission="positions.read">
              <PositionManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/organization/settings"
          element={
            <ProtectedRoute requiredPermission="settings.read">
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/system/status"
          element={
            <ProtectedRoute requiredPermission="system.status">
              <SystemStatusPage />
            </ProtectedRoute>
          }
        />
      </Route>
    </Routes>
  );
}

const sectionRoutes = navigationConfig.flatMap(section =>
  (section.children || []).map(child => ({
    path: child.path,
    permission: child.permission,
    sectionPath: section.path,
  }))
);

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ToastProvider>
          <AdminRoutes />
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;